import copy from "clipboard-copy";
import find from "lodash/find";
import get from "lodash/get";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import { RillContext } from "../../rill/RillReact/share/base";
import getAvatarUrl from "../../utils/get-avatar-url";
import { getOrganizationRoles } from "../../utils/organization-roles";
import {
  calculateUserFreeTrialDaysLeft,
  isCoach,
  isCoachFreeTrial,
  isPerformClient,
  isPullActiveCoach,
} from "../../utils/user-helpers";
import { getRoleNameById } from "../../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import Select from "../inputs/select";
import Modal from "../modal-new";
import SaveInputButtons from "../SaveInputButtons";

class ProfileModal extends React.Component {
  static URL_REGEX =
    /^(?:(?:https?):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;
  constructor(props, context) {
    super(props, context);
    this.user =
      context.locals.admin_profileUser ||
      find(context.locals.admin_allUsers, ["_id", context.locals.query.user]);

    this.state = {
      plan: this.user.coachingBilling?.plan
        ? this.user.coachingBilling.plan.id
        : "",
      permissions: get(this.user, "permissions", []),
      askForCreditCard: this.user.coachingBilling
        ? this.user.coachingBilling.requestPaymentInfoOnNextLogin
        : true,
      referralLink: this.user.referralProgramInfo?.link || "",
      isEditingReferralLink: false,
    };
    this.handleClick = this.handleClick.bind(this);
    this.updateVideoUploadPermission =
      this.updateVideoUploadPermission.bind(this);
    this.updateCustomMobileIconPermission =
      this.updateCustomMobileIconPermission.bind(this);
    this.updateCustomAppStoreListingPermission =
      this.updateCustomAppStoreListingPermission.bind(this);
    this.removeCoach = this.removeCoach.bind(this);
    this.activatePerform = this.activatePerform.bind(this);
    this.cancelPerform = this.cancelPerform.bind(this);
    this.copyPerformBillingLink = this.copyPerformBillingLink.bind(this);
  }

  handlePermissionToggle({ target: { checked, name } }) {
    const permissions = Object.assign([], this.state.permissions);
    if (!checked && permissions.includes(name))
      permissions.splice(permissions.indexOf(name), 1);
    if (checked && !permissions.includes(name)) permissions.push(name);

    this.setState(() => ({ canSave: true, permissions }));
  }

  savePermissions() {
    let planInfoIndex, plan;
    if (this.state.plan) {
      planInfoIndex = this.props.stripePlans.findIndex((plan) => {
        return plan.id === this.state.plan;
      });
      plan = this.props.stripePlans[planInfoIndex];
    }

    submit("/actions/user/update-permission-plan", {
      method: "POST",
      body: {
        userId: this.user._id,
        permissions: this.state.permissions,
        plan,
        askForCreditCard: this.state.askForCreditCard,
        email: this.user.email,
      },
    });
  }

  handlePlanChange(value) {
    this.setState({ plan: value, canSave: true });
  }

  handleAskForCreditCardToggle(e) {
    this.setState({
      askForCreditCard: e.target.checked,
      canSave: true,
    });
  }

  /**
   * Update the referral link value
   * @param {*} e
   */
  handleReferralLinkChange(e) {
    const value = e.target.value;
    this.setState((prevState) => ({
      ...prevState,
      referralLink: value,
    }));
  }

  /**
   * Reset the referral Link value
   * @param {*} e
   */
  handleResetReferralLink(e) {
    e.preventDefault();
    this.setState((prevState) => ({
      ...prevState,
      referralLink: this.user.referralProgramInfo?.link || "",
      isEditingReferralLink: false,
    }));
  }

  /**
   * Validate if referral link was changed
   * @returns
   */
  referralLinkChanged = () => {
    const currentLink = this.state.referralLink || "";
    const userLink = this.user.referralProgramInfo?.link || "";
    return currentLink !== userLink && ProfileModal.URL_REGEX.test(currentLink);
  };

  /**
   * Function to close forever the container who shows the referral link information
   * @param {*} e
   */
  handleUpdatereferralLink = (e) => {
    const showReferralButton =
      this.user.referralProgramInfo?.showReferralButton ?? "";
    e.preventDefault();
    submit("/actions/user/update-referral-program", {
      method: "POST",
      body: {
        purpose: "update-link",
        userId: this.user._id,
        referralProgramInfo: {
          link: this.state.referralLink,
          showReferralButton,
        },
      },
    });
  };

  handleClick(e, email) {
    e.preventDefault();
    copy(email);
  }

  updateVideoUploadPermission(e) {
    if (confirm(this.props.t("profileModal.confirmVideoUploadPermission"))) {
      submit("/actions/user/update-video-upload-permission", {
        method: "POST",
        body: {
          userId: this.user._id,
          videoUploadForbidden: Number(!this.user.videoUploadForbidden),
        },
      });
    }
  }

  updateCustomMobileIconPermission() {
    if (confirm(this.props.t("profileModal.confirmCustomMobileIcon"))) {
      submit("/actions/user/update-custom-mobile-icon-permission", {
        method: "POST",
        body: {
          userId: this.user._id,
          customMobileIcon: Number(!this.user.customMobileIcon),
        },
      });
    }
  }

  updateCustomAppStoreListingPermission() {
    if (confirm(this.props.t("profileModal.confirmAppStoreListing"))) {
      submit("/actions/user/update-custom-appstore-listing-permission", {
        method: "POST",
        body: {
          userId: this.user._id,
          appStoreListingEnable: Number(!this.user.appStoreListingEnable),
        },
      });
    }
  }

  removeCoach() {
    if (confirm(this.props.t("profileModal.confirmRemoveCoach"))) {
      submit("/actions/user/remove-coach", {
        method: "POST",
        body: {
          athleteId: this.user._id,
        },
      });
    }
  }

  activatePerform() {
    if (
      confirm(
        "Are you sure you want to activate PERFORM for this coach? They will be billed using their existing card on file."
      )
    ) {
      submit("/actions/user/activate-perform", {
        method: "POST",
        body: {
          userId: this.user._id,
        },
      });
    }
  }

  cancelPerform() {
    if (
      confirm(
        "Are you sure you want to cancel PERFORM for this coach? All their PERFORM clients will be deactivated."
      )
    ) {
      submit("/actions/user/cancel-perform", {
        method: "POST",
        body: {
          userId: this.user._id,
        },
      });
    }
  }

  copyPerformBillingLink() {
    const billingUrl = `${globalThis.location.origin}/perform-billing`;
    copy(billingUrl);
  }

  render() {
    const { t } = this.props;
    const { locals } = this.context;
    const { momentTZ } = locals;
    const stripePlans = this.props.stripePlans;
    const user = this.user;
    const askForCreditCard = this.state.askForCreditCard;
    const referralLink = this.state.referralLink;
    const avatarURL = getAvatarUrl(user);

    const isSubscriber = user.subscription;
    const isMember = user.membership;
    const isPerform = isPerformClient(user);
    const isCoachUser = isCoach(user);
    const isCoachTrial = isCoachFreeTrial(user);
    const isFe = user.pushAccountInfo;
    const isGym = user.pressAccountInfo;
    const coachTrialLeft =
      isCoachUser && isFe && isCoachTrial
        ? calculateUserFreeTrialDaysLeft(user)
        : null;
    const isPullNoVerified =
      (!isCoachUser &&
        !isFe &&
        !isSubscriber &&
        !isPerform &&
        !user.coach &&
        user.coachingInvite) ||
      user.setup !== "finished";
    const showToken = user.setup !== "finished" && user.token;

    const coachData =
      isSubscriber || isMember || isPerform
        ? this.context.locals.athlete
        : null;

    const isOrganizationUser =
      !!user.organizationId && !!user.organizationRoleId;
    const organizationRoles = getOrganizationRoles();
    const organizationRoleName = isOrganizationUser
      ? getRoleNameById(organizationRoles, user.organizationRoleId)
      : "Coach";

    const EmailContainer = ({ email, id = false }) => (
      <div className="email-container">
        <div className={`profile-email ${id ? "id" : ""}`}>{email}</div>
        <img
          alt={t("profileModal.copyAlt")}
          src="/img/svg/clipboard.svg"
          style={{
            width: "auto",
            height: id ? "20px" : "24px",
            marginLeft: "0.2vw",
            cursor: "pointer",
          }}
          onClick={(evt) => this.handleClick(evt, email)}
        />
      </div>
    );

    return (
      <Modal
        toClear={["user"]}
        width="600px"
        title={t("profileModal.title")}
        className={"admin-profile"}
      >
        <div className="body">
          <div className="Profile-Preview">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "flex",
                }}
              >
                <div className="avatar">
                  <img src={avatarURL} alt="avatar" />
                </div>

                <div
                  style={{
                    marginLeft: 10,
                    width: "calc(100% - 190px)",
                  }}
                >
                  <div
                    className="name"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <b>{user.name || t("profileModal.noName")}</b>
                    </div>
                    <EmailContainer email={user.email} />
                  </div>
                  <div
                    style={{
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <b style={{ marginRight: "10px" }}>
                      {t("profileModal.idLabel")}:
                    </b>{" "}
                    <EmailContainer email={user._id} id />
                  </div>
                  {showToken && (
                    <div
                      style={{
                        textAlign: "left",
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <b style={{ marginRight: "10px" }}>
                        {t("profileModal.tokenLabel")}:
                      </b>{" "}
                      <EmailContainer email={user.token} id />
                    </div>
                  )}
                  {user.country && (
                    <div style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                      <b>{t("profileModal.countryLabel")}:</b> {user.country}
                    </div>
                  )}
                  <div style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                    <b>{t("profileModal.memberSince")}:</b>{" "}
                    {momentTZ(user.registered).format("MMMM, YYYY")}
                  </div>
                  <div style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                    <b>{t("profileModal.typeLabel")}:</b>{" "}
                    {isCoachUser
                      ? isFe
                        ? isGym
                          ? t("profileModal.pushPressCoach")
                          : t("profileModal.pushCoach")
                        : t("profileModal.pullCoach")
                      : isSubscriber
                        ? t("profileModal.pushAthlete")
                        : isMember
                          ? t("profileModal.pressAthlete", {
                              access: user.membership.access,
                            })
                          : isPerform
                            ? t("profileModal.performAthlete")
                            : user.coach
                              ? t("profileModal.pullAthlete")
                              : t("profileModal.legacyAthlete")}
                    {(isSubscriber || isMember || isPerform) && (
                      <>
                        <div
                          style={{ textAlign: "left", marginBottom: "0.5rem" }}
                        >
                          <b>{t("profileModal.coachId")}:</b> {coachData._id}
                        </div>
                        <div
                          style={{ textAlign: "left", marginBottom: "0.5rem" }}
                        >
                          <b>{t("profileModal.coachName")}:</b> {coachData.name}
                        </div>
                        <div
                          style={{ textAlign: "left", marginBottom: "0.5rem" }}
                        >
                          <b>{t("profileModal.coachEmail")}:</b>
                          <EmailContainer email={coachData.email} />
                        </div>
                      </>
                    )}
                  </div>
                  {isCoachUser && isCoachTrial && coachTrialLeft > -1 && (
                    <div style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                      <b>{t("profileModal.freeTrial")}:</b> {coachTrialLeft}{" "}
                      {t("profileModal.daysLeft")}
                    </div>
                  )}
                  {isFe && (
                    <>
                      <div
                        style={{ textAlign: "left", marginBottom: "0.5rem" }}
                      >
                        <b>{t("profileModal.subscribers")}:</b>{" "}
                        {user.subscribers ? user.subscribers.length : 0}
                      </div>
                      {isGym ? (
                        <div
                          style={{ textAlign: "left", marginBottom: "0.5rem" }}
                        >
                          <b>{t("profileModal.members")}:</b>{" "}
                          {user.members ? user.members.length : 0}
                        </div>
                      ) : null}
                      <div
                        style={{ textAlign: "left", marginBottom: "0.5rem" }}
                      >
                        <b>{t("profileModal.stripeId")}:</b>{" "}
                        {user.pushAccountInfo.connectedAccountId}
                      </div>
                      <div
                        className="click-admin-toggle"
                        onClick={this.updateVideoUploadPermission}
                      >
                        <b>{t("profileModal.videoUploadForbidden")}:</b>{" "}
                        {user.videoUploadForbidden
                          ? t("profileModal.on")
                          : t("profileModal.off")}
                      </div>
                    </>
                  )}

                  {(isFe || isCoachUser) && (
                    <div
                      className="click-admin-toggle"
                      onClick={this.updateCustomMobileIconPermission}
                    >
                      <b>{t("profileModal.customMobileIcon")}:</b>{" "}
                      {user.customMobileIcon
                        ? t("profileModal.on")
                        : t("profileModal.off")}
                    </div>
                  )}

                  {(isFe || isCoachUser) && (
                    <div
                      className="click-admin-toggle"
                      onClick={this.updateCustomAppStoreListingPermission}
                    >
                      <b>{t("profileModal.enableAppStoreListing")}:</b>{" "}
                      {user.appStoreListingEnable
                        ? t("profileModal.enabled")
                        : t("profileModal.disabled")}
                    </div>
                  )}

                  {!isCoachUser &&
                    !isFe &&
                    !isSubscriber &&
                    !isMember &&
                    user.coach && (
                      <>
                        <div
                          style={{ textAlign: "left", marginBottom: "0.5rem" }}
                        >
                          <b>{t("profileModal.coach")}:</b> {user.coach.name}{" "}
                          <span
                            className="text-red-500 cursor-pointer"
                            onClick={this.removeCoach}
                          >
                            ({t("common:remove")})
                          </span>
                        </div>
                        <div
                          style={{ textAlign: "left", marginBottom: "0.5rem" }}
                        >
                          <b>{t("profileModal.coachEmail")}:</b>{" "}
                          <EmailContainer email={user.coach.email} />
                        </div>
                      </>
                    )}
                  {isPullNoVerified && (
                    <div style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                      <b>{t("profileModal.invitedAthleteNotVerified")}</b>
                    </div>
                  )}
                  <div style={{ textAlign: "left", marginBottom: "0.5rem" }}>
                    <b>{t("profileModal.organization")}:</b>{" "}
                    {isOrganizationUser
                      ? t("profileModal.yes")
                      : t("profileModal.no")}
                  </div>
                  {isOrganizationUser && (
                    <>
                      <div
                        style={{ textAlign: "left", marginBottom: "0.5rem" }}
                      >
                        <b>{t("profileModal.organizationId")}:</b>{" "}
                        {user.organizationId}
                      </div>
                      <div
                        style={{ textAlign: "left", marginBottom: "0.5rem" }}
                      >
                        <b>{t("profileModal.role")}:</b> {organizationRoleName}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {isCoachUser && (
                <div className="mt-4 p-4 border border-(--white-10) border-solid rounded-md">
                  {user.performBilling?.status === "active" ||
                  user.performBilling?.status === "past_due" ? (
                    <div className="flex justify-between items-center">
                      <div>
                        <b>PERFORM:</b>{" "}
                        <span
                          style={{
                            color:
                              user.performBilling.status === "active"
                                ? "#5bc500"
                                : "orange",
                          }}
                        >
                          {user.performBilling.status === "active"
                            ? "Active"
                            : "Past Due"}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={this.cancelPerform}
                        data-button-type="secondary"
                        data-action="cancel"
                      >
                        Cancel PERFORM
                      </button>
                    </div>
                  ) : isPullActiveCoach(user) ? (
                    <div className="flex justify-between items-center">
                      <div>
                        <b>PERFORM:</b> Inactive
                      </div>
                      <button
                        type="button"
                        onClick={this.activatePerform}
                        data-button-type="primary"
                      >
                        Activate PERFORM
                      </button>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div>
                        <b>PERFORM:</b> Inactive{" "}
                        <span style={{ color: "#a0aec0", fontSize: "13px" }}>
                          — No card on file
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={this.copyPerformBillingLink}
                        data-button-type="secondary"
                      >
                        Copy Billing Link
                      </button>
                    </div>
                  )}
                </div>
              )}

              {this.context.locals.stripeRecentEvents.length ? (
                <div>
                  <table className="recent-activity">
                    <thead>
                      <tr>
                        <th>{t("profileModal.typeHeader")}</th>
                        <th>{t("profileModal.descriptionHeader")}</th>
                        <th>{t("profileModal.dateHeader")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.context.locals.stripeRecentEvents.map(
                        ({ type, description, created }) => (
                          <tr key={`${type}-${description}-${created}`}>
                            <td>{type}</td>
                            <td>{description}</td>
                            <td>{created}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            <div className="roles">
              <div className="col">
                <label className="checkbox-input">
                  <input
                    type="checkbox"
                    name="admin"
                    onClick={(e) => this.handlePermissionToggle(e)}
                    defaultChecked={
                      user.admin || user.permissions?.includes("admin")
                    }
                  />

                  <span className="text">{t("profileModal.admin")}</span>
                </label>
                <label className="checkbox-input">
                  <input
                    type="checkbox"
                    name="coach"
                    onClick={(e) => this.handlePermissionToggle(e)}
                    defaultChecked={user.permissions?.includes("coach")}
                  />

                  <span className="text">
                    {t("profileModal.coachingPortalCoach")}
                  </span>
                </label>
              </div>

              <div className="col">
                <label className="checkbox-input">
                  <input
                    type="checkbox"
                    name="ambassador"
                    onClick={(e) => this.handlePermissionToggle(e)}
                    defaultChecked={
                      user.ambassador ||
                      user.permissions?.includes("ambassador")
                    }
                  />

                  <span className="text">{t("profileModal.ambassador")}</span>
                </label>
                <label className="checkbox-input">
                  <input
                    type="checkbox"
                    name="coachCanSubmit"
                    onClick={(e) => this.handlePermissionToggle(e)}
                    defaultChecked={user.permissions?.includes(
                      "coachCanSubmit"
                    )}
                  />

                  <span className="text">{t("profileModal.premiumCoach")}</span>
                </label>
              </div>
            </div>
            {this.state.permissions?.includes("coach") && (
              <div className="referral-link-container">
                <label htmlFor="referral-link">
                  {t("profileModal.referralLinkLabel")}
                </label>
                <input
                  name="referral-link"
                  className="text-input h-10!"
                  placeholder={t("profileModal.referralLinkPlaceholder")}
                  value={referralLink}
                  onChange={(e) => this.handleReferralLinkChange(e)}
                  onFocus={() =>
                    this.setState((prevState) => ({
                      ...prevState,
                      isEditingReferralLink: true,
                    }))
                  }
                />
                <SaveInputButtons
                  enabled={this.state.isEditingReferralLink}
                  handleSave={(e) => {
                    this.handleUpdatereferralLink(e);
                  }}
                  handleDismiss={(e) => this.handleResetReferralLink(e)}
                  saveDisabled={!this.referralLinkChanged()}
                />
              </div>
            )}
            {this.state.permissions?.includes("coach") && (
              <div className="plan-select mt-4">
                <Select
                  name="plan"
                  className="select-input"
                  title={t("profileModal.selectPlanTitle")}
                  value={this.state.plan}
                  onChange={(e) => this.handlePlanChange(e)}
                  options={stripePlans.map((plan) => {
                    return {
                      value: plan.id,
                      text: plan.nickname,
                    };
                  })}
                ></Select>
                <label className="checkbox-input">
                  <input
                    type="checkbox"
                    name="askForCreditCard"
                    onChange={(e) => this.handleAskForCreditCardToggle(e)}
                    defaultChecked={askForCreditCard}
                  />

                  <span className="text">
                    {t("profileModal.askForCreditCardNextLogin")}
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        <footer>
          <a
            role="button"
            data-button-type="secondary"
            data-action="cancel"
            href="/actions/refresh?modal="
          >
            {t("common:cancel")}
          </a>
          <button
            onClick={(e) => this.savePermissions(e)}
            type="submit"
            data-button-type="primary"
            disabled={this.state.canSave ? undefined : true}
          >
            {t("common:save")}
          </button>
        </footer>
      </Modal>
    );
  }
}

ProfileModal.contextType = RillContext;

export default withTranslation("coaching-portal")(ProfileModal);
